'use client';

import { useState } from 'react';
import type { WebAppClusterDto } from '@terra/shared-contracts-external-network';
import {
  seedWebAppConfirmState,
  type WebAppClusterDecision,
} from './detect-web-apps-confirm';
import type { WebAppReviewMode } from './web-app-types';

export const useWebAppReviewState = ({
  clusters,
  clustersLoading,
}: {
  clusters: WebAppClusterDto[];
  clustersLoading: boolean;
}) => {
  const [reviewMode, setReviewMode] = useState<WebAppReviewMode | null>(null);
  const [hasSeeded, setHasSeeded] = useState(false);
  const [decisions, setDecisions] = useState<
    Record<string, WebAppClusterDecision>
  >({});
  const [linkChoices, setLinkChoices] = useState<Record<string, string>>({});

  if (reviewMode === null && hasSeeded) {
    setHasSeeded(false);
  }

  if (reviewMode !== null && !hasSeeded && !clustersLoading) {
    const seed = seedWebAppConfirmState(clusters);

    setHasSeeded(true);
    setDecisions(seed.decisions);
    setLinkChoices(seed.linkChoices);
  }

  const changeDecision = ({
    similarityKey,
    decision,
    suggestedApplicationId,
  }: {
    similarityKey: string;
    decision: WebAppClusterDecision;
    suggestedApplicationId?: string;
  }) => {
    setDecisions((current) => ({
      ...current,
      [similarityKey]: decision,
    }));

    if (
      decision !== 'link' ||
      linkChoices[similarityKey] ||
      !suggestedApplicationId
    ) {
      return;
    }

    setLinkChoices((current) => ({
      ...current,
      [similarityKey]: suggestedApplicationId,
    }));
  };

  const changeLinkChoice = ({
    similarityKey,
    applicationId,
  }: {
    similarityKey: string;
    applicationId: string;
  }) => {
    setLinkChoices((current) => ({
      ...current,
      [similarityKey]: applicationId,
    }));
  };

  return {
    reviewMode,
    setReviewMode,
    decisions,
    linkChoices,
    changeDecision,
    changeLinkChoice,
  };
};
